import {
	observable,
	computed,
	action,
	reaction,
	runInAction,
	IReactionDisposer,
} from "mobx";

import { Subscription } from "rxjs";
import {
	IMenuSubscriptionRequest,
	EventFilterParams,
	ICountSubscription,
} from "@gp/hub";
import { LiveStatus, LiveStreamStatus } from "@gp/models";
import { SessionStorageProvider, history, redirect } from "@state";

import { BaseOfferMenuStore } from "../shared/BaseOfferMenuStore";
import { SportMenuNodeType } from "@data-types/offer/shared/SportNodeType";
import NodeStore from "@state/stores/offer/shared/NodeStore";
import { LivePeriodSubMenuStore } from "@state/stores/offer/live/LivePeriodSubMenuStore";
import PlayedLiveEventsStore from "@state/stores/offer/live/PlayedLiveEventsStore";
import FavoritesStore from "@state/stores/offer/shared/FavoritesStore";
import RootStore from "@state/stores/RootStore";
import { ToggleStore } from "@state/stores/offer/shared/ToggleStore";
import { StorageStateKeysEnum } from "@utils/storage";
import { TSection } from "@data-types/offer/live/Section";

export class LiveOfferMenuStore extends BaseOfferMenuStore {
	public type: "LiveOfferMenuStore" = "LiveOfferMenuStore";
	/** Used to remember what was selected in the menu. */
	protected _rememberSelectionSegmentsString: string | null = null;
	protected _rememberSelectedSport: this["menu"] | null = null;
	private fetchDataDisposer: IReactionDisposer | null;
	private favoriteTournamentsDisposer: IReactionDisposer | null = null;
	private favoriteTournamentsCountDisposer: IReactionDisposer | null = null;

	favoritesStore: FavoritesStore;
	public periodSubMenu: LivePeriodSubMenuStore;
	public liveEventsFromMyBets: PlayedLiveEventsStore;
	liveMenuSubscription: Subscription | null;
	liveFavoriteTournamentsSubscription: Subscription | null = null;
	menuCountSubscription: any;

	private liveMenuSubscriptionFilter: IMenuSubscriptionRequest | null = null;

	@observable selectedSport: NodeStore<SportMenuNodeType> | null = null;

	@observable public liveEventsCount = 0;
	@observable public upcomingEventsCount = 0;
	@observable public totalHighlightsCount = 0;
	@observable public liveStreamEventCount = 0;
	@observable public liveFavoriteTournamentsCount = 0;
	@observable private isLoadedMenuAllLive = false;

	@computed get selectedPeriod() {
		return this.periodSubMenu.selectedPeriod;
	}
	@computed get period() {
		return this.periodSubMenu.selectedPeriodKey;
	}

	@computed get liveFavoritesCount() {
		return this.favoritesStore.liveFavoritesCounter;
	}

	@computed get isMenuAllLive() {
		return [
			TSection.Matches,
			TSection.MyFavoritesEvents,
			TSection.MyFavoritesTournaments,
			TSection.PlayedEvents,
		].includes(this.periodSubMenu.selectedPeriodKey);
	}

	@observable menu = new NodeStore<SportMenuNodeType>({
		id: "root",
		count: 0,
		type: "root",
		name: "",
	});

	@observable groupedSports = observable.set();

	//#region  constructor

	constructor(rootStore: RootStore, favoritesStore: FavoritesStore) {
		super(rootStore);

		this.periodSubMenu = new LivePeriodSubMenuStore(this);
		this.favoritesStore = favoritesStore;
		this.liveEventsFromMyBets = new PlayedLiveEventsStore(this.rootStore);
		const preservedSportIds = JSON.parse(
			SessionStorageProvider.get(
				StorageStateKeysEnum.MOBILE_GROUPED_SPORTS_ID
			) || "[]"
		);

		if (preservedSportIds.length) {
			this.groupedSports = observable.set(preservedSportIds);
		}
	}

	@action.bound
	onBeforeInitialize() {
		// Offer menu should be closed by default on live page
		this.closeOfferMenu();
	}

	//#endregion constructor

	//#region sportCollapse

	@observable collapsedSportsStore = new ToggleStore();

	@action.bound
	expandSport(sportId: string): void {
		this.collapsedSportsStore.elements.delete(sportId);
	}

	@action.bound
	collapseSport(sportId: string): void {
		this.collapsedSportsStore.elements.add(sportId);
	}

	@action.bound
	toggleSportCollapse(sportId: string): void {
		this.collapsedSportsStore.toggleCollapse(sportId);
	}

	@action.bound
	clearCollapsedSports(): void {
		this.collapsedSportsStore.elements.clear();
	}

	//#endregion sportCollapse

	//#region tournament collapse

	@observable collapsedTournamentsStore = new ToggleStore();

	@action.bound
	expandTournament(tournamentId: string): void {
		this.collapsedTournamentsStore.elements.delete(tournamentId);
	}

	@action.bound
	collapseTournament(tournamentId: string): void {
		this.collapsedTournamentsStore.elements.add(tournamentId);
	}

	@action.bound
	toggleTournamentCollapse(tournamentId: string): void {
		if (this.collapsedTournamentsStore.elements.has(tournamentId)) {
			this.collapsedTournamentsStore.elements.delete(tournamentId);
		} else {
			this.collapsedTournamentsStore.elements.add(tournamentId);
		}
	}

	@action.bound
	collapseAllTournaments(tournamentIds: string[]): void {
		for (const id of tournamentIds) {
			this.collapsedTournamentsStore.add(id);
		}
	}

	@action.bound
	expandAllTournaments(tournamentIds: string[]): void {
		for (const id of tournamentIds) {
			this.collapsedTournamentsStore.remove(id);
		}
	}

	@action.bound
	clearCollapsedTournaments(): void {
		this.collapsedTournamentsStore.elements.clear();
	}

	//#endregion tournament collapse

	// #region tournament grouping

	@action.bound
	addTournamentGroupingSportId(id: string) {
		this.groupedSports.add(id);
		SessionStorageProvider.set(
			StorageStateKeysEnum.MOBILE_GROUPED_SPORTS_ID,
			JSON.stringify(Array.from(this.groupedSports))
		);
	}

	@action.bound
	removeTournamentGroupingSportId(id: string) {
		this.groupedSports.delete(id);
		SessionStorageProvider.set(
			StorageStateKeysEnum.MOBILE_GROUPED_SPORTS_ID,
			JSON.stringify(Array.from(this.groupedSports))
		);
	}

	@action.bound
	toggleTournamentGrouping(id: string) {
		if (this.groupedSports.has(id)) {
			this.removeTournamentGroupingSportId(id);
			return;
		}
		this.addTournamentGroupingSportId(id);
	}

	// #endregion tournament grouping
	//#region sport menu
	@computed
	public get isOfferMenuOpen(): boolean {
		return this.rootStore.isSideMenuExpanded;
	}

	@computed get isExpandDisabled(): boolean {
		return (
			[
				TSection.MyFavoritesEvents,
				TSection.MyFavoritesTournaments,
			].includes(this.periodSubMenu.selectedPeriodKey) ||
			this.menu.children.length <= 0
		);
	}

	@action.bound
	toggleOpenOfferMenu(): void {
		if (this.isOfferMenuOpen) {
			this.closeOfferMenu();
		} else {
			this.openOfferMenu();
		}
	}

	@action.bound
	openOfferMenu(): void {
		this.rootStore.openSideMenu();
	}

	@action.bound
	closeOfferMenu(): void {
		if (history.action === "POP") {
			this.selectedSport = this._rememberSelectedSport;
		} else {
			this.selectedSport = null;
		}
		this.rootStore.closeSideMenu();
	}

	/** Store current menu selection in state so we can reset to it on history back. */
	@action.bound
	rememberSelection() {
		// This may be called multiple times, we only want to do it once when using event switcher
		if (
			this._rememberSelectionSegmentsString == null &&
			history.action === "PUSH"
		) {
			this._rememberSelectionSegmentsString = this.menu.urlStructure;
			this._rememberSelectedSport = this.selectedSport;
		}
	}

	//#endregion sport menu

	//#region  fetch data
	//###################

	@action.bound
	async fetchData() {
		this.disposeFetchData();
		this.liveEventsFromMyBets.onInitialize();

		this.createMenuCountSubscription();
		this.fetchDataDisposer = reaction(
			() => ({ period: this.periodSubMenu.selectedPeriodKey }),
			() => {
				this.createMenuSubscription();
			},
			{
				fireImmediately: true,
			}
		);
		this.favoriteTournamentsDisposer = reaction(
			() => ({
				isMenuAllLive: this.isMenuAllLive,
				ids: [...this.favoritesStore.userFavoriteTournamentsSet],
			}),
			this.createFavoriteTournamentsMenuSubscription,
			{
				fireImmediately: true,
			}
		);
		this.favoriteTournamentsCountDisposer = reaction(
			() =>
				this.isMenuAllLive && this.isLoadedMenuAllLive
					? this.menuFavoriteTournamentsCount
					: null,
			(count) => {
				if (count != null) {
					this.liveFavoriteTournamentsCount = count;
				}
			},
			{
				fireImmediately: true,
			}
		);
	}

	//#region live menu subscription

	@action.bound
	createMenuSubscription() {
		const subscriptionRequest: IMenuSubscriptionRequest =
			this.generateSubscriptionRequestForMenuSubscription();
		if (
			JSON.stringify(subscriptionRequest) ===
				JSON.stringify(this.liveMenuSubscriptionFilter) &&
			this.liveMenuSubscription != null
		) {
			// If filter did not change and subscription exists, don't dispose menu subscription to create a new one
			return;
		}

		this.disposeLiveMenuSubscription();

		this.liveMenuSubscriptionFilter = subscriptionRequest;

		let isFirst = true;
		const isAllLive = this.isMenuAllLive;

		this.liveMenuSubscription = this.rootStore.hub
			.getMenuSubscription(subscriptionRequest)
			.subscribe({
				next: (response) => {
					runInAction(() => {
						this.assignMenu(response.menu);
						this.isLoadedMenuAllLive = isAllLive;
						this.selectFirstSportOnFirstResponse(isFirst);
						isFirst = false;
						this.onDoneFetching();
					});
				},
				error: (err) => {
					console.error(err);
					this.onDoneFetching();
				},
			});
	}

	@computed private get menuFavoriteTournamentsCount() {
		const favorites = this.favoritesStore.userFavoriteTournamentsSet;
		if (favorites.size === 0) {
			return 0;
		}

		let count = 0;
		for (const sport of this.menu.children) {
			for (const category of sport.children) {
				for (const tournament of category.children) {
					if (favorites.has(tournament.node.id)) {
						count++;
					}
				}
			}
		}
		return count;
	}

	@action.bound
	createFavoriteTournamentsMenuSubscription() {
		this.disposeFavoriteTournamentsMenuSubscription();

		if (this.isMenuAllLive) {
			return;
		}

		const favorites = this.favoritesStore.userFavoriteTournamentsSet;
		if (favorites.size === 0) {
			this.liveFavoriteTournamentsCount = 0;
			return;
		}

		this.liveFavoriteTournamentsSubscription = this.rootStore.hub
			.getMenuSubscription({
				subscriptionId: "live-favorite-tournaments-menu",
				filter: {
					liveStatus: LiveStatus.LIVE,
					tournamentId: { eq: [...favorites] },
				},
			})
			.subscribe({
				next: (response) => {
					let count = 0;
					for (const sport of response.menu) {
						for (const category of sport.subItems) {
							count += category.subItems.length;
						}
					}
					runInAction(() => {
						this.liveFavoriteTournamentsCount = count;
					});
				},
				error: (err) => {
					console.error(err);
				},
			});
	}

	generateSubscriptionRequestForMenuSubscription(): IMenuSubscriptionRequest {
		const filter: EventFilterParams = {};
		if (this.isMenuAllLive) {
			filter.liveStatus = LiveStatus.LIVE;
		} else if (this.periodSubMenu.selectedPeriodKey === TSection.Upcoming) {
			filter.liveStatus = LiveStatus.PREMATCH;
			filter.isUpcoming = true;
		} else if (
			this.periodSubMenu.selectedPeriodKey === TSection.Highlights
		) {
			filter.liveStatus = LiveStatus.LIVE;
			filter.isTopEvent = true;
		} else if (
			this.periodSubMenu.selectedPeriodKey === TSection.LiveStream
		) {
			filter.liveStreamStatus = {
				eq: [LiveStreamStatus.Live, LiveStreamStatus.Upcoming],
			};
			filter.liveStatus = LiveStatus.LIVE;
		}

		return {
			subscriptionId: "live-sports-menu",
			filter: filter,
		};
	}

	//#endregion live menu subscription

	//#region live menu counts subscriptions

	@action.bound
	createMenuCountSubscription() {
		this.disposeMenuCountSubscription();

		const menuCountSubscription: ICountSubscription = {
			subscriptionId: "live-tabs-count",
			count: {
				live: {
					liveStatus: 1,
				},
				upcoming: {
					liveStatus: 0,
					isUpcoming: true,
				},
				highlights: {
					liveStatus: 1,
					isTopEvent: true,
				},
				liveStream: {
					liveStreamStatus: {
						eq: [LiveStreamStatus.Live, LiveStreamStatus.Upcoming],
					},
					liveStatus: LiveStatus.LIVE,
				},
			},
		};

		this.menuCountSubscription = this.hub
			.getCounts(menuCountSubscription)
			.subscribe({
				next: (response) => {
					const liveCount = Number(response.live);
					if (!isNaN(liveCount)) {
						this.liveEventsCount = liveCount;
					}
					const upcomingCount = Number(response.upcoming);
					if (!isNaN(upcomingCount)) {
						this.upcomingEventsCount = upcomingCount;
					}
					const highlightsCount = Number(response.highlights);
					if (!isNaN(highlightsCount)) {
						this.totalHighlightsCount = highlightsCount;
					}
					const liveStreamCount = Number(response.liveStream);
					if (!isNaN(liveStreamCount)) {
						this.liveStreamEventCount = liveStreamCount;
					}
				},
				error: (err) => {
					console.error(err);
					this.onDoneFetching();
				},
			});
	}

	//#endregion live menu counts subscriptions

	@action.bound
	protected selectFirstSportOnFirstResponse(isFirst: boolean) {
		// Do nothing for desktop theme
	}

	//#endregion fetch data
	//#####################

	//#region update params

	@action.bound
	setRouterMatchObj(routerMatch: {
		params: {
			period: TSection;
			segments: string;
		};
	}): void {
		const { params } = routerMatch;

		// On history back reset current menu to what was remember;
		if (history.action === "POP") {
			this.resetMenuOnUrlParamsFromRouter(
				this._rememberSelectionSegmentsString
			);
			this.selectedSport = this._rememberSelectedSport;
			this._rememberSelectionSegmentsString = null;
		}

		this.setParams(params);
	}

	@action.bound
	setParams(
		params: {
			period: TSection;
			segments: string;
		},
		shouldResetMenuOnUrl: boolean = false
	) {
		const { period } = params;

		if (shouldResetMenuOnUrl) {
			this.resetMenuOnUrlParamsFromRouter(params.segments);
		}

		if (
			period !== this.periodSubMenu.selectedPeriodKey &&
			![
				TSection.MyFavoritesEvents,
				TSection.MyFavoritesTournaments,
			].includes(this.periodSubMenu.selectedPeriodKey) &&
			period != TSection.Matches
		) {
			this.menu.onCheck(false);
			this.selectedSport = null;
		}
		this.periodSubMenu.setPeriodKey(period);
	}

	//#endregion update params

	//#region sideMenu actions overrides

	/** Toggles is sport expanded in side menu. */
	@action.bound
	toggleSport(node: NodeStore<SportMenuNodeType>) {
		if (this.isMenuAllLive) {
			if (this.selectedSport?.node?.id !== node.node.id) {
				this.menu.onCheck(false);
				this.selectSport(node);
				window.scrollTo(0, 0);
				return;
			}

			if (this.isOfferMenuOpen) {
				this.rootStore.closeSideMenu();
			} else {
				this.rootStore.openSideMenu();
			}

			return;
		}

		if (node?.node?.id === this.selectedSport?.node?.id) {
			this.toggleOpenOfferMenu();
		} else {
			this.selectSport(node);
			this.openOfferMenu();
		}
	}

	/** @param {NodeStore} node which is expanded in menu. */
	@action.bound
	setSelectedSport(node: NodeStore<SportMenuNodeType>) {
		this.selectedSport = node;
	}

	/** Adds sport to side menu and expands side menu. */
	@action.bound
	selectSport(node: NodeStore<SportMenuNodeType>) {
		this.selectedSport = node;
		this.collapsedSportsStore.elements.delete(node.node.id);

		if (this.isMenuAllLive) {
			this.selectedSport?.onCheck(true);
			this.rootStore.closeSideMenu();
			redirect(TSection.Matches, true);
		} else {
			this.openOfferMenu();
		}
	}

	/** Removes sport from side menu and closes side menu. */
	@action.bound
	deselectSport() {
		this.selectedSport = null;
		this.closeOfferMenu();
	}

	//#endregion sideMenu actions overrides

	//#region disposers

	@action.bound
	onDispose() {
		this.onBaseDispose();
		this.liveEventsFromMyBets.onDispose();

		this.favoritesStore.favoriteEventsStoreCounter.disposeSubscription();

		this.disposeMenuCountSubscription();

		this.disposeLiveMenuSubscription();
		this.disposeFavoriteTournamentsMenuSubscription();
		this.disposeFetchData();
		this.resetObservables();
	}

	@action.bound
	resetObservables() {
		this.periodSubMenu.selectedPeriodKey = TSection.Matches;
		this.liveEventsCount = 0;
		this.totalHighlightsCount = 0;
		this.upcomingEventsCount = 0;
		this.liveStreamEventCount = 0;
		this.liveFavoriteTournamentsCount = 0;
		this.isLoadedMenuAllLive = false;
		this.isStoreInitialized = false;

		this.liveMenuSubscriptionFilter = null;
		this.groupedSports.clear();
	}

	@action.bound
	disposeLiveMenuSubscription() {
		if (this.liveMenuSubscription != null) {
			this.liveMenuSubscription.unsubscribe();
			this.liveMenuSubscription = null;
		}
	}

	@action.bound
	disposeFavoriteTournamentsMenuSubscription() {
		if (this.liveFavoriteTournamentsSubscription != null) {
			this.liveFavoriteTournamentsSubscription.unsubscribe();
			this.liveFavoriteTournamentsSubscription = null;
		}
	}

	@action.bound
	disposeFetchData() {
		if (this.fetchDataDisposer != null) {
			this.fetchDataDisposer();
			this.fetchDataDisposer = null;
		}
		if (this.favoriteTournamentsDisposer != null) {
			this.favoriteTournamentsDisposer();
			this.favoriteTournamentsDisposer = null;
		}
		if (this.favoriteTournamentsCountDisposer != null) {
			this.favoriteTournamentsCountDisposer();
			this.favoriteTournamentsCountDisposer = null;
		}
	}

	@action.bound
	disposeMenuCountSubscription() {
		if (this.menuCountSubscription != null) {
			this.menuCountSubscription.unsubscribe();
			this.menuCountSubscription = null;
		}
	}

	@action.bound
	disposeMenu() {
		this.menu.onDispose();
	}

	//#endregion disposers

	//#region played live events

	@computed get isPlayedLiveEventsCountEnabled() {
		return this.liveEventsFromMyBets.isEnabled;
	}
	@computed get playedLiveEventsCount() {
		return this.liveEventsFromMyBets.count;
	}

	//#endregion played live events
}
